import React from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { grey } from '../../../colors'
import { type ISectionSettings, type ISurveySection } from '../../../store/slices/SurveyTemplateSlice'
import { getSurveySections } from '../../../store/slices/SurveyTemplateSlice/selectors'
import IsRepeatPerInstructor from './IsRepeatPerInstructor'
import AttributesFilterChips from '../../../components/AttributesFilterChips'
import { DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES, sortTeachingRolesByPrecedence } from '../constant'

interface FilterSettingsProps {
  section: ISurveySection
}

const FilterSettings: React.FC<FilterSettingsProps> = ({ section }) => {
  const theme = useTheme()
  const sectionsList = getSurveySections()
  // Branched sections can inherit repeat-per-instructor display state from the parent even
  // when the child has not saved its own settings payload yet.
  const parentSection = sectionsList.find((currentSection) => currentSection.surveySectionId === section.parentSurveySectionId)
  const parentSettings = parentSection?.settings
  const parentDefaultSetting = Array.isArray(parentSettings) ? parentSettings[0] : undefined
  const parentRepeatPerInstructorEnabled = parentDefaultSetting?.isRepeatPerInstructor ?? false

  const sortedParentSelectedRoles = sortTeachingRolesByPrecedence(parentDefaultSetting?.selectedRoles ?? [])
  // If the parent repeats per instructor, show the child as repeating with the parent's roles.
  const inheritedChildSetting: ISectionSettings[] = section.parentSurveySectionId && parentRepeatPerInstructorEnabled
    ? [{
        isRepeatPerInstructor: true,
        selectedRoles: sortedParentSelectedRoles.length > 0
          ? sortedParentSelectedRoles
          : DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES,
        isSelectInstructorEnabled: parentDefaultSetting?.isSelectInstructorEnabled ?? false
      }]
    : []

  // Prefer the section's saved settings, otherwise fall back to inherited child display state.
  const sectionSettings: ISectionSettings[] = section.parentSurveySectionId && parentRepeatPerInstructorEnabled
    ? inheritedChildSetting
    : section.settings?.length
      ? section.settings
      : inheritedChildSetting

  const firstSetting = sectionSettings[0]
  const settingsWithAttributes = sectionSettings.filter((setting) => (setting?.attributes?.length ?? 0) > 0)

  const shouldShowAccordion = !section.parentSurveySectionId && settingsWithAttributes.length > 0

  const filterChipCount =
    settingsWithAttributes.length === 0
      ? ''
      : `(${settingsWithAttributes.length})`

  if (sectionSettings.length === 0) {
    return null
  }

  return (
    <>
      <IsRepeatPerInstructor settings={firstSetting} />
      {shouldShowAccordion && (
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            mt: theme.spacing(4),
            border: `1px solid ${theme.palette.borderColor}`,
            borderRadius: theme.spacing(2),
            '&::before': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: grey[250] }} />}
          >
            <Typography fontSize={14} fontWeight={600} color={grey[250]}>
              Display condition groups {filterChipCount}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: theme.spacing(3), pb: theme.spacing(3), pt: 0 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
              </Box>

              {settingsWithAttributes?.map((setting, index: number) => (
                <Box key={`${index}-${setting?.attributes?.length ?? 0}`} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <Box display={'flex'} gap={2} alignItems={'center'}>
                    <Typography fontSize={14} fontWeight={600} color={grey[250]} sx={{ whiteSpace: 'nowrap' }}>
                        Group {index + 1}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" minWidth={0} minHeight={32}>
                      <AttributesFilterChips attributes={setting?.attributes ?? []} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}

export default FilterSettings
